import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Sparkles, 
  TrendingUp, 
  AlertTriangle, 
  Briefcase, 
  GraduationCap,
  Globe,
  Award
} from "lucide-react";

// Embedded mock data for the landing page demo
const mockResume = {
  ai_summary: "Experienced senior developer with 8 years in React/Node.js stack. Strong track record of leading cross-functional teams and delivering scalable applications. Excellent communication skills demonstrated through technical blog writing.",
  hiring_recommendation: "Strong Hire",
  job_match_score: 92,
  skills: ["React", "TypeScript", "Node.js", "PostgreSQL", "AWS", "GraphQL", "Docker", "CI/CD"],
  strengths: [
    "8+ years experience in full-stack development",
    "Led multiple successful product launches",
    "Strong open-source contributions"
  ],
  concerns: [
    "No direct experience with Kubernetes",
    "Career gap of 6 months in 2022"
  ],
  total_years_experience: 8,
  highest_degree: "M.Sc Computer Science",
  extracted_location: "San Francisco, CA",
  career_trajectory: "Upward"
};

export function LandingResumeDemo() {
  return (
    <div className="space-y-4">
      {/* AI Summary Section */}
      <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">AI Summary</span>
          </div>
          <Badge variant="default" className="bg-green-600">
            {mockResume.hiring_recommendation}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{mockResume.ai_summary}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Job Match</span>
          <Progress value={mockResume.job_match_score} className="flex-1 h-2" />
          <span className="text-sm font-medium text-primary">{mockResume.job_match_score}%</span>
        </div>
      </div>

      {/* Skills */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Skills</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {mockResume.skills.map((skill) => (
            <Badge key={skill} variant="secondary" className="text-xs">
              {skill}
            </Badge>
          ))}
        </div>
      </div>

      {/* Strengths & Concerns */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-green-600">
            <TrendingUp className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Strengths</span>
          </div>
          <ul className="space-y-1">
            {mockResume.strengths.map((strength, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <span className="text-green-600 mt-0.5">•</span>
                {strength}
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Areas to Explore</span>
          </div>
          <ul className="space-y-1">
            {mockResume.concerns.map((concern, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <span className="text-amber-600 mt-0.5">•</span>
                {concern}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Separator />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Experience</p>
            <p className="text-sm font-medium">{mockResume.total_years_experience} years</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Education</p>
            <p className="text-sm font-medium">{mockResume.highest_degree}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Location</p>
            <p className="text-sm font-medium">{mockResume.extracted_location}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">Trajectory</p>
            <p className="text-sm font-medium">{mockResume.career_trajectory}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
